/**
 * Sales Qualification Hook
 * Manages submission of comprehensive enterprise qualification data
 */

import { useState } from 'react';
import { salesQualificationService } from '@/services/entities/onboarding';
import { SalesQualificationData } from '../../../types/onboarding/salesQualification';

export interface UseSalesQualificationReturn {
  isSubmitting: boolean;
  submitQualification: (
    data: SalesQualificationData,
    tier?: 'silver' | 'gold' | 'custom',
    addons?: string[],
  ) => Promise<{ success: boolean; assessmentId?: string; message: string; errors?: string[] }>;
  error: string | null;
  clearError: () => void;
}

export const useSalesQualification = (): UseSalesQualificationReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitQualification = async (
    data: SalesQualificationData,
    tier: 'silver' | 'gold' | 'custom' = 'custom',
    addons: string[] = [],
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await salesQualificationService.submitQualification(data, tier, addons);

      if (!result.success) {
        setError(result.message);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unexpected error occurred';
      setError(errorMessage);

      return {
        success: false,
        message: errorMessage,
        errors: [errorMessage],
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    isSubmitting,
    submitQualification,
    error,
    clearError,
  };
};
